import { Form, FormInstance } from 'antd';
import React from 'react';
import { Gutter, Icon, SecondaryButton, InfoTooltip } from '@common/components';
import { useQuery } from '@common/hooks';
import { WORKS_ORGANIZATION_UNITS_QUERY } from '@institutions/graphql';
import { Colors } from 'works/legacy/lib/style-helper';
import { AllowListSelectItem } from './_allow-list-select-item';

export function AllowListSelect({
  form,
  disabled,
  selectedRoleId,
  roleOptions,
}: {
  form: FormInstance;
  disabled?: boolean;
  selectedRoleId?: string;
  roleOptions?: { label: string; value: string }[];
}) {
  const { data: organizationUnitData } = useQuery(
    WORKS_ORGANIZATION_UNITS_QUERY
  );

  const organizationUnitOptions = [
    ...(organizationUnitData?.organizationUnits || []),
  ]
    ?.map((unit) => {
      const label = unit.costCenter
        ? `${unit.name} - ${unit.costCenter}`
        : unit.name;

      return {
        label: label,
        value: unit.id,
      };
    })
    ?.sort((a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label)
    );

  const isAllowedUnitHomeUnit = (index: number) => {
    const allowedUnits = form.getFieldValue('allowedUnits');
    return allowedUnits[index].isHomeUnit;
  };

  const handleChangeHomeUnit = (isChecked: boolean, changedIndex: number) => {
    const allowedUnits = form.getFieldValue('allowedUnits');

    if (isChecked) {
      allowedUnits.forEach(
        (allowedUnit: { isHomeUnit: boolean }, index: number) => {
          allowedUnit.isHomeUnit = index === changedIndex;
        }
      );
    } else {
      allowedUnits[changedIndex].isHomeUnit = false;
    }

    form.setFieldsValue({ allowedUnits });
  };

  const validateAtLeastOneAllowedUnit = async () => {
    const allowedUnits = form.getFieldValue('allowedUnits');
    if (!allowedUnits || allowedUnits.length === 0) {
      throw new Error('Please add at least one approved unit and role');
    }
  };

  return (
    <Gutter direction="bottom">
      <Form.Item
        label="Units"
        name="approvedUnits"
        colon={false}
        required
        dependencies={['active', 'allowedUnits']}
        rules={[{ validator: validateAtLeastOneAllowedUnit }]}
      >
        <Form.List name="allowedUnits">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <AllowListSelectItem
                  form={form}
                  length={fields.length}
                  key={key}
                  index={name}
                  remove={remove}
                  isAllowedUnitHomeUnit={isAllowedUnitHomeUnit}
                  handleChangeHomeUnit={handleChangeHomeUnit}
                  roleOptions={roleOptions}
                  organizationUnitOptions={organizationUnitOptions}
                />
              ))}

              <SecondaryButton
                icon={<Icon icon="plus" kind="regular" />}
                onClick={() =>
                  add({
                    unit: undefined,
                    roles: [selectedRoleId],
                    isHomeUnit: fields.length === 0,
                  })
                }
                disabled={disabled}
              >
                Unit
              </SecondaryButton>

              {!selectedRoleId && (
                <InfoTooltip
                  size="1x"
                  leftOffset={5}
                  iconColor={Colors.elm}
                  title="To add Units you must select a Role."
                />
              )}
            </>
          )}
        </Form.List>
      </Form.Item>
    </Gutter>
  );
}
